import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { CONTACT } from "../lib/api";
import { BrandLogo } from "./BrandLogo";
import LanguageSwitcher from "./LanguageSwitcher";

const LINKS = [
  { key: "services", href: "/#diensten" },
  { key: "about", href: "/#over-ons" },
  { key: "area", href: "/#werkgebied" },
  { key: "reviews", href: "/#reviews" },
  { key: "projects", href: "/#projecten" },
  { key: "blog", href: "/blog", isRoute: true },
  { key: "quote", href: "/#offerte" },
  { key: "contact", href: "/#contact" },
];

export default function Navbar() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (isAdmin) return null;

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#0A1128]/95 backdrop-blur-md border-b border-white/10 shadow-lg shadow-black/20" : "bg-[#0A1128]"
      }`}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-5 md:px-10 h-16 md:h-20">
        <BrandLogo size="md" />

        <nav className="hidden xl:flex items-center gap-7">
          {LINKS.map((l) => {
            const cls = "relative text-white/85 hover:text-white text-[13px] uppercase font-semibold tracking-wider transition-colors group py-1";
            const inner = (
              <>
                {t(`nav.${l.key}`)}
                <span className="absolute -bottom-0.5 left-0 w-0 h-[2px] bg-[#FF5A1F] group-hover:w-full transition-all duration-300" />
              </>
            );
            return l.isRoute ? (
              <Link key={l.href} to={l.href} data-testid={`nav-link-${l.key}`} className={cls}>{inner}</Link>
            ) : (
              <a key={l.href} href={l.href} data-testid={`nav-link-${l.href.replace(/[^a-z0-9]/gi,'')}`} className={cls}>{inner}</a>
            );
          })}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <LanguageSwitcher variant="dark" testIdSuffix="-desktop" />
          <a
            href={`tel:${CONTACT.phone}`}
            data-testid="nav-call-btn"
            className="btn-primary text-xs md:text-sm"
          >
            <Phone className="w-4 h-4" /> {t("nav.call")}
          </a>
        </div>

        <div className="flex xl:hidden items-center gap-2">
          <div className="md:hidden">
            <LanguageSwitcher variant="dark" testIdSuffix="-mobile" />
          </div>
          <button
            className="text-white p-2"
            onClick={() => setOpen(!open)}
            data-testid="nav-menu-toggle"
            aria-label={t("nav.menu")}
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="xl:hidden bg-[#0A1128] border-t border-white/10 overflow-hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="px-5 py-4 flex flex-col gap-1">
              {LINKS.map((l) => {
                const cls = "text-white/90 hover:text-[#FF5A1F] uppercase font-semibold text-sm tracking-wide py-3 border-b border-white/5";
                const label = t(`nav.${l.key}`);
                return l.isRoute ? (
                  <Link
                    key={l.href}
                    to={l.href}
                    onClick={() => setOpen(false)}
                    data-testid={`nav-mobile-link-${l.key}`}
                    className={cls}
                  >
                    {label}
                  </Link>
                ) : (
                  <a
                    key={l.href}
                    href={l.href}
                    onClick={() => setOpen(false)}
                    data-testid={`nav-mobile-link-${l.href.replace(/[^a-z0-9]/gi,'')}`}
                    className={cls}
                  >
                    {label}
                  </a>
                );
              })}
              <a
                href={`tel:${CONTACT.phone}`}
                onClick={() => setOpen(false)}
                className="btn-primary justify-center mt-3 text-sm"
                data-testid="nav-mobile-call-btn"
              >
                <Phone className="w-4 h-4" /> {t("nav.call")} — {CONTACT.phoneDisplay}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
